import React from "react";
import {useMessages} from "i18n";
import {useTheme} from "styled-components";
import BaseInput from "components/BaseInput";
import {CardSheet} from "components/CardSheet";
import BrandColorPalette from "../../account/brand/BrandColorPalette";
import {PALETTE_PRESETS} from "../../account/brand/brandPalette";
import {HiddenFileInput} from "../../account/brand/BrandSettings.styles";
import {
	AddImageLabel, Counter, ImageRow, ImageTile, TileImage,
} from "../../account/brand/PageThemeSection.styles";
import {TileRemoveButton} from "../../account/brand/TileRemoveButton";
import {HiddenRadio, placeholderArtUrls} from "../../sponsor/BackdropTemplatePicker.styles";
import {SponsorOptionsPanel} from "../../sponsor/options/SponsorOptionsPanel";
import {optionErrorText} from "../../sponsor/options/optionErrorText";
import {useMountAfterOpen} from "utils/useMountAfterOpen";
import {THEME_IMAGE_ACCEPT_ATTRIBUTE} from "../../account/brand/themeValidation";
import {LOGO_ACCEPT_ATTRIBUTE} from "../../account/brand/brandValidation";
import type {PersonalizationVM} from "../personalization/usePersonalizationDraft";
import type {PersonalizationField} from "../personalization/personalizationDraft";
import {personalizationFontFamily} from "../personalization/personalizationFont";
import {
	personalizationPreview, previewInheritance, previewPageFor, type RequestPageDraft,
} from "../personalization/personalizationPreview";
import {fontFamilies} from "../../theme/tokens";
import {loadPersonalizationFontSpecimens} from "../personalization/recipientFontLoader";
import {
	personalizationComposerFooter, personalizationComposerGate, personalizationComposerIsLive,
	personalizationComposerShowsFields, personalizationLookChip,
} from "../personalization/personalizationOffer";
import {
	accentClearLabelKey, logoPickLabelKey, personaliseCommitAction, templateImagesLine,
} from "../personalization/personalizationBuilder";
import {
	ComposeField, FieldHint, FooterRoot, OptionBlock, Section, SectionLabel,
} from "./OptionsSheet.styles";
import {
	BuilderControls, BuilderLayout, BuilderPreview, DismissFooterAction, DoneFooterAction,
	DraftError, EntitlementChip, FontCard, FontDiacritics, FontGrid, FontName, FontSpecimen, GuestOffer,
	GuestOfferCopy, LabelRow, LogoPickGlyph, LogoPickTile, LogoRemoveButton, LogoTile, LookChipRow,
	OriginBadge, GuestHint, GuestPlanNote, SignInFooterAction, SignInInlineAction, SpreadFooterActions,
} from "./PersonaliseSheet.styles";
import {PersonalisePreview} from "./PersonalisePreview";
import type {SenderChoiceControls} from "./useSenderChoice";
import type {CompanyFields} from "sender/senderChoice";
import {PersonaliseSenderFields} from "./PersonaliseSenderFields";
import {TemplateTilePicker} from "./TemplateTilePicker";

/**
 * The logo tile's file input.
 *
 * An id rather than a name, because the tile's accessible name is STATE ("Choose a logo" until one is
 * set, "Replace logo" after) since ui#1942 made the picture the picker. Specs that assert what this
 * control does — whether a given rung may work it at all — need a handle that does not move with the
 * draft. The name itself is asserted separately, where the flip is the subject.
 */
export const PERSONALISE_LOGO_PICK_TESTID = "personalise-logo-pick";

/**
 * The radio group's form name, shared by every animation tile in this builder.
 *
 * Its own name rather than the sponsor's or the account picker's: same-name radios are ONE exclusive
 * group to the browser however they are nested, so two pickers sharing a name would deselect each other
 * whenever both were mounted.
 */
const TEMPLATE_RADIO_NAME = "transfer-personalise-template";

interface Props {
	/** Whether the sheet is open — owned by whichever surface raised it. */
	open: boolean;
	/**
	 * Close the non-modal sheet, leaving the draft exactly as it is.
	 *
	 * This is the COMMIT path and the band's eviction path, not the sender's dismissal — see
	 * {@link CardSheetProps.onDismiss} and the `dismiss` note below.
	 */
	onClose: () => void;
	/** Release the owning surface's single editor slot after the exit transition. */
	onExited: () => void;
	/** Whether an ordinary close should return focus to the button that opened this editor. */
	restoreFocusOnClose: boolean;
	/** The opener captured before the panel makes its sender controls inert. */
	returnFocus: HTMLElement | null;
	/** The personalization view-model — the whole contract (epic #26). */
	vm: PersonalizationVM;
	/**
	 * How many files this transfer is carrying, for the miniature (ui#1942).
	 *
	 * Passed in rather than read here: this sheet is mounted twice, once by the create panel and once
	 * over the success card, and each of those owners already holds the selection. What the number
	 * decides — the files head's count and whether the download button promises one file or all of them
	 * — is `personalizationPreview`'s rule, not this view's.
	 */
	fileCount: number;
	/**
	 * Who the transfer or request being composed goes out as (kitecorp/transferify-ui#3140, #3186): "Your name" then
	 * carries the choice of a person or a company, and the miniature names the sender as the page will. Held by the
	 * home panel. Null, or left out, over the success card, where the transfer has already gone out as whoever it
	 * was sent as and only its look can still be attached.
	 */
	senderChoice?: SenderChoiceControls | null;
	/**
	 * The company a transfer already sent went out as, over the success card (kitecorp/transferify-ui#3186): the
	 * miniature names it as the download page does, and "Your name" is not offered, because a company is named by the
	 * company and the look attached there goes without a name. Null, or left out, for a person's transfer.
	 */
	sentCompany?: CompanyFields | null;
	/**
	 * The panel asks for files rather than sending them: every line that names a transfer names the request, and the
	 * miniature draws the upload page the request's contact opens rather than a download page
	 * (kitecorp/transferify-ui#3173).
	 */
	forRequest?: boolean;
	/**
	 * The request being composed, as far as its upload page shows it: its note and how long it takes files, as the
	 * request composer last reported them. Read only while {@link forRequest}; null until the composer has reported.
	 */
	requestPage?: RequestPageDraft | null;
	/**
	 * Raises the login flow from the locked preview (ui#764). Owned by the panel, which already holds
	 * the login modal for every other gated CTA in the create flow — a route would unmount the create
	 * flow and destroy the in-progress transfer.
	 */
	onSignIn: () => void;
	/** The sender kept what they composed. Fires on the commit only, never on a dismissal. */
	onCommitted: () => void;
}

/**
 * The per-transfer personalization builder (ui#556, api#461; reshaped by ui#1814, brought to the
 * owner-approved prototype by kitecorp/transferify-ui#1953).
 *
 * A DUMB view. Every rule it appears to apply — what this costs, which looks exist and how they group,
 * how many images the chosen one takes, which faces are on offer, whether the draft may be sent, which
 * fields are inherited — is answered by {@link PersonalizationVM}, itself a thin adapter over the
 * React-free `personalizationDraft` / `personalizationOffer` / `personalizationFont` /
 * `personalizationTemplateGroups` modules.
 *
 * **Nothing here is behind a paywall any more (owner decision 2026-09-01).** ui#1409 dimmed every
 * control until a one-off was bought, on the reasoning that paying buys the right to compose; the owner
 * reversed it, because nobody buys a cosmetic they have never seen. Every signed-in sender composes the
 * whole page and watches it as they go, and since kitecorp/transferify#525 every plan includes the look, so
 * there is no plan question at all. The words and the look each carry the same "Free" chip on their label.
 *
 * **The sheet is a working surface, not a page (ui#1953).** A plain bold title with no explainer, short
 * uppercase section labels carrying their chip instead of a paragraph each, the animation picker as
 * compact tiles two to a row with the live miniature as the only preview, and a footer of two pills.
 * Every sentence the old sheet spent introducing a control has gone; the tile's description survives as
 * its tooltip and as what a screen reader hears after the name.
 *
 * It REUSES the account personalization sheet's editors where they fit: the same disc colour picker,
 * the same image-tile grammar and the same template copy (`account.theme.templates`), because these are
 * the same treatments and two implementations of one picker drift on the first change to either.
 *
 * The claimed address is not here: it stays Premium, in Brand settings and the pricing table, and is not
 * part of a single transfer's look (kitecorp/transferify#513).
 */
export function PersonaliseSheet({
	open, onClose, onExited, restoreFocusOnClose, returnFocus, vm, fileCount, senderChoice = null, sentCompany = null,
	forRequest = false, requestPage = null, onSignIn, onCommitted,
}: Props) {
	const {sender, account, common, nav} = useMessages();
	// While the panel asks for files the builder dresses a request, and says so wherever it names a transfer.
	const copy = forRequest ? {...sender.personalise, ...sender.personalise.forRequest} : sender.personalise;
	/**
	 * One option control's message, in the SENDER's words (kitecorp/transferify-api-service#1149).
	 *
	 * `account.theme.optionErrors`, the catalogue kitecorp/transferify-ui#1869 wrote for the page-theme
	 * picker, rather than the advertiser-facing sponsor one: this is the same reader choosing the same
	 * settings for their own page, one surface over. The VERDICT is `vm.optionErrors`, which is
	 * `validateSponsorOptions`' — this only names it.
	 *
	 * `logo` never has one here: the panel is mounted with no mark control, so no refusal can be
	 * addressed to it.
	 */
	const errorTextFor = optionErrorText(vm.optionErrors, account.theme.optionErrors);
	const colorLabelId = React.useId();
	const fontLabelId = React.useId();
	/** Names the required-pictures line so Done can carry the reason it refuses (ui#2416). */
	const imagesRequiredId = React.useId();
	/**
	 * The pictures control, so a Done that cannot commit can put the sender ON it (ui#2416).
	 *
	 * The file input rather than its label: the input IS the control since ui#1942, it carries the
	 * accessible name, and it is what a keyboard user needs focus on to open the picker. The section
	 * beside it is scrolled as well, because the input is visually hidden and a sender who never
	 * scrolled this far needs to SEE the tiles they are being sent to.
	 */
	const addImageRef = React.useRef<HTMLInputElement>(null);
	const imagesSectionRef = React.useRef<HTMLDivElement>(null);
	const theme = useTheme();
	const placeholders = placeholderArtUrls(theme.tokens);
	/*
	 * The miniature's props, derived DURING RENDER from the same draft the controls write (ui#1921).
	 *
	 * No effect, no memo, no local copy of anything: a facet the sender changes is in `vm.draft` before
	 * this line runs, so the miniature is repainted by the very render the edit caused. Anything that
	 * cached it here would be the thing that eventually shows a page one keystroke behind.
	 */
	// The request's upload page, once the composer has reported what the request holds, which it does as it mounts,
	// before its own Personalise button can open this sheet. Which page is drawn decides what the miniature
	// inherits: a request's upload page carries the request's own look alone.
	const request = forRequest && senderChoice !== null && requestPage !== null
		? {sender: senderChoice.sender, ...requestPage}
		: null;
	const page = previewPageFor(request !== null);
	const preview = personalizationPreview(vm.draft, previewInheritance(page, vm.inherited), {
		placeholderArtUrls: placeholders,
		standardFontStack: fontFamilies.sans,
		fileCount,
	});
	// WHO MAY EDIT, answered by the React-free ladder rather than here. Two rungs remain: the signed-out
	// preview (an api rule — every personalization endpoint is authenticated), and everyone else.
	const gate = personalizationComposerGate(vm.access);
	const locked = gate === "locked";
	const composing = personalizationComposerShowsFields(gate, vm.availability);
	// The controls ANSWER to this sender: editable, and a commit may arm what they composed.
	const live = personalizationComposerIsLive(gate, vm.availability);
	const inert = !live;
	/**
	 * The branded half is offered wherever this sheet is (kitecorp/transferify-api-service#1172): the plan
	 * question waits for the send, so there is nothing about the upload pass that hides it. One builder,
	 * both passes.
	 */
	const branding = composing;
	const bodyReady = useMountAfterOpen(open);
	// The line answers what the SENDER did, and nothing else. Two errors are excluded, both because the
	// section that owns them already says it and an alert on top would be noise: `tooFewImages` (the
	// line under the tiles states the requirement in place, where the animation was chosen, and the
	// images counter carries the tally) and `templateUnavailable` (the picker renders ONLY the looks
	// this brand offers).
	const draftError = live && vm.error !== null
		&& vm.error !== "tooFewImages" && vm.error !== "templateUnavailable"
		? vm.error
		: null;
	/**
	 * The ONE line under the animation tiles: an invitation, the requirement, or the plain arity.
	 *
	 * `templateImagesLine` decides which, so what a sender is told about a MANDATORY step is a rule
	 * with tests rather than a ternary in this markup (ui#2416).
	 */
	const imagesLine = templateImagesLine(vm.arity, vm.draft.images.length);
	const imagesRequired = branding && imagesLine.kind === "required";

	/**
	 * The specimens have to be drawn IN the faces they offer, so the picker declares all of them.
	 *
	 * Deliberately keyed on the picker being MOUNTED rather than on the sheet opening: a signed-out
	 * visitor's rung renders no font section, and a recipient's page must fetch the ONE face its sender
	 * chose — which is the owner's LCP constraint and why `recipientFontLoader` keeps the two entry
	 * points apart.
	 */
	React.useEffect(() => {
		if (branding && bodyReady && vm.fonts.length > 0) {
			void loadPersonalizationFontSpecimens(vm.fonts);
		}
	}, [branding, bodyReady, vm.fonts]);

	const originBadge = (field: PersonalizationField, label: string) => {
		const origin = vm.originOf(field);
		if (origin === "unset") {
			return null;
		}
		return <OriginBadge aria-label={copy.originAria(label, copy.origin[origin])}>{copy.origin[origin]}</OriginBadge>;
	};

	/*
	 * The look's chip, on the label of the section that holds it (kitecorp/transferify-ui#1953): the same "Free"
	 * as the words' chip, because every plan includes the look (kitecorp/transferify#525). Whether it shows is
	 * `personalizationLookChip`'s answer.
	 */
	const entitlementChip = personalizationLookChip(gate, vm.availability) === "free"
		? <EntitlementChip>{copy.freeChip}</EntitlementChip>
		: null;

	// WHICH ACTIONS EXIST, answered by the same React-free ladder as everything else this sheet reads.
	const footerActions = personalizationComposerFooter(gate, vm.availability);

	/**
	 * The commit, and — since ui#1409 removed the enable toggle — the ONLY thing that arms a draft.
	 *
	 * Committing keeps the composed look; {@link dismiss} sends the ordinary one. Arming HERE and not on
	 * open is the same promise `emptyPersonalizationDraft` makes: opening the builder must never silently
	 * attach a personalization to a transfer.
	 *
	 * ARMING IS GATED ON `live`. A signed-out visitor and a sender whose offer has not answered yet
	 * both have an unsendable draft on hand, so arming theirs would turn the panel's send guard on
	 * permanently with no control on screen they could use to satisfy it. For the LOCKED preview the
	 * commit is therefore a plain close, and it stays on screen because the pitch is the point of that
	 * rung (ui#764).
	 *
	 * AND IT CAN REFUSE (ui#2416, owner live-testing 2026-09-12). An animation whose pictures are
	 * missing produces no request at all, so arming it and closing left the sender looking at an
	 * unchanged screen: no dialog, no message, nothing. `personaliseCommitAction` is the rule;
	 * this only carries out its answer.
	 */
	const commit = () => {
		const action = personaliseCommitAction({live, error: vm.error});
		if (action === "fixImages") {
			// The press is ANSWERED, never swallowed. The requirement is already stated under the tiles,
			// so what is left is to put the sender on the control that satisfies it — and to bring that
			// control into view, because the input itself is visually hidden behind its tile.
			imagesSectionRef.current?.scrollIntoView({block: "nearest"});
			addImageRef.current?.focus();
			return;
		}
		if (action === "commit") {
			vm.setEnabled(true);
			onCommitted();
		}
		onClose();
	};

	/**
	 * Dismissing the builder = send the ordinary page: the personalization is disarmed and the transfer
	 * proceeds with the default look.
	 *
	 * ONE function behind FOUR gestures since ui#1408 — the footer's Discard, the ✕, Escape and the
	 * outside click — because they are one intention. `setEnabled(false)` DISARMS rather than erases: the
	 * sections stay on screen with the images, the note and the chosen animation still in them, and
	 * reopening shows exactly what was composed. Only the commit re-arms it, so a sender who dismisses by
	 * mistake has lost nothing.
	 *
	 * The sender choice beside "Your name" is the one thing it does clear (owner 2026-10-02: "discard should clear
	 * those too"): back to a person, the company's name and tax ID emptied, so a discarded builder never sends files
	 * or a request as a company nobody confirmed.
	 */
	const dismiss = () => {
		vm.setEnabled(false);
		senderChoice?.discard();
		onClose();
	};

	const footer = (
		<FooterRoot>
			{/* All footer actions cluster on the reading edge (owner 2026-08-12, ui#855); the way out
			    precedes the commit in DOM, visual and keyboard order, as the options sheet's pair does. */}
			<SpreadFooterActions data-testid="personalise-actions">
				{footerActions.cancel && (
					<DismissFooterAction type="button" variant="secondary" onClick={dismiss}>
						{copy.dismiss}
					</DismissFooterAction>
				)}
				{footerActions.signIn && (
					<SignInFooterAction type="button" variant="primary" onClick={onSignIn}>
						{nav.login}
					</SignInFooterAction>
				)}
				{footerActions.commit && (
					/* ENABLED even while the draft is refused (ui#2416). A disabled Done would be a second
					   dead control beside the first: it takes no press, announces no reason, and a sender
					   who never scrolled to the pictures would be left guessing why. It presses through
					   instead, onto the control that fixes it — and carries the reason for a screen-reader
					   user who has not reached that line yet. */
					<DoneFooterAction type="button" variant="primary" onClick={commit} data-testid="personalise-commit"
						aria-describedby={imagesRequired ? imagesRequiredId : undefined}>
						{copy.done}
					</DoneFooterAction>
				)}
			</SpreadFooterActions>
		</FooterRoot>
	);

	return (
		<CardSheet
			open={open}
			onClose={onClose}
			// The ✕, Escape and the outside click all drop the draft (ui#1408) — see `dismiss` above for
			// why they are one intention, and `CardSheetProps.onDismiss` for why an EVICTION must not join
			// them: another sheet claiming the band is not the sender saying "never mind".
			onDismiss={dismiss}
			onExited={onExited}
			restoreFocusOnClose={restoreFocusOnClose}
			returnFocus={returnFocus}
			title={copy.title}
			// A plain bold title and no explainer (ui#1953): this head sits above a two-column working
			// surface, not above a page of reading matter — see `CardSheetProps.titleTone`.
			titleTone="plain"
			closeLabel={common.close}
			footer={footer}
			// A transient editor raised from a button, so `dialog` — deliberately WITHOUT `aria-modal`.
			// CardSheet is non-modal and adds no scrim, keeping the top bar live and in the accessibility
			// tree while a sender personalises a transfer.
			role="dialog"
			// The one WIDE sheet in the app (ui#1942). Its body is genuinely two columns — the controls
			// and a live miniature of the recipient's page — and at the shared 600px that miniature's art
			// was about 30% of a 252px column, which is the "not very usable" the owner reported.
			width="wide"
			// A name a spec can still use while a modal dialog is open over it (ui#1282): an `aria-modal`
			// overlay puts the whole page behind it under `aria-hidden`, so role+name stops resolving
			// exactly when "is the builder still there?" is the question being asked.
			testId="personalise-sheet"
		>
			{/* Shell-first (ui#836): the opening commit paints ONLY the dialog frame; this whole body —
			    the live miniature above all — mounts on the next frame. */}
			{bodyReady && <BuilderLayout $withPreview={composing}>
			{/*
			  * The live miniature (ui#1921), FIRST in the DOM because that is the phone order: there it
			  * is a dock at the top of the scroll region rather than a second column. Only once there is
			  * something to compose — a signed-out visitor's rung offers no controls, so a preview of a
			  * page they cannot change yet would be a picture with nothing behind it.
			  */}
			{composing && (
				<BuilderPreview>
					<PersonalisePreview
						preview={preview}
						company={senderChoice === null ? sentCompany
							: senderChoice.sender.kind === "company" ? senderChoice.sender.company : null}
						request={request}
					/>
				</BuilderPreview>
			)}
			{/* Addressed by id so a spec can measure the column the prototype fixes at 380px (ui#1953). */}
			<BuilderControls data-testid="personalise-controls">
			{/* What stands ABOVE the sections, only when there is something to say: the locked visitor's
				pitch and way in, or a refusal of what the sender did.
			    Rendered conditionally rather than as an always-present block, because an empty first
			    section still costs its padding and the head above already carries the title. */}
			{(locked || draftError !== null) && (
				<Section>
					<OptionBlock>
						{/* A LOCKED visitor is told every plan includes the look, and invited in, because every
						    entitlement chip below reads an offer they were never entitled to fetch. */}
						{locked && (
							<GuestOffer>
								<GuestOfferCopy>
									<GuestPlanNote>{copy.guestIncludedNote}</GuestPlanNote>
									<GuestHint>{copy.guestHint}</GuestHint>
								</GuestOfferCopy>
								<SignInInlineAction type="button" variant="secondary" onClick={onSignIn}>
									{nav.login}
								</SignInInlineAction>
							</GuestOffer>
						)}
						{draftError !== null && <DraftError role="alert">{copy.errors[draftError]}</DraftError>}
					</OptionBlock>
				</Section>
			)}

			{/* THE FREE RUNG (owner decision 2026-09-01): the sender's own words, on every plan.
			    FIRST in the sheet, because it is what every sender can actually use and what the
			    preview-first flow is built on. Title and Note moved here out of
			    the transfer options sheet in the same decision; there is no second copy of them left.
			    Three fields, the name first (owner's interim answer on ui#1953; whether Title and Note
			    fold into one "Message" is the open question recorded on that ticket). */}
			{composing && (
				<Section data-testid="personalise-free">
					<LabelRow>
						<SectionLabel>{copy.freeLabel}</SectionLabel>
						<EntitlementChip>{copy.freeChip}</EntitlementChip>
					</LabelRow>
					{/* No name for a transfer that already went out as a company: the company names it. */}
					{(senderChoice !== null || sentCompany === null) && (
						<ComposeField>
							{senderChoice !== null ? (
								<PersonaliseSenderFields
									controls={senderChoice}
									name={vm.draft.displayName}
									onNameChange={vm.setDisplayName}
									disabled={inert}
								/>
							) : (
								<BaseInput
									label={copy.nameLabel}
									placeholder={copy.namePlaceholder}
									value={vm.draft.displayName}
									disabled={inert}
									onChange={event => vm.setDisplayName(event.target.value)}
								/>
							)}
						</ComposeField>
					)}
					<ComposeField>
						<BaseInput
							label={sender.titleField}
							value={vm.draft.title}
							disabled={inert}
							onChange={event => vm.setTitle(event.target.value)}
						/>
					</ComposeField>
					<ComposeField>
						<BaseInput
							label={copy.messageLabel}
							placeholder={copy.messagePlaceholder}
							multiline
							minRows={2}
							value={vm.draft.message}
							disabled={inert}
							onChange={event => vm.setMessage(event.target.value)}
						/>
					</ComposeField>
				</Section>
			)}

			{branding && (
				<div data-testid="personalise-branding">
					{/* The branded half's chip, at the TOP of that half (owner 2026-09-12). It used to sit on the
					    animation row, which stopped being the first branded control when the colour swatch moved up. */}
					{entitlementChip !== null && <LookChipRow>{entitlementChip}</LookChipRow>}
					<Section>
						<LabelRow>
							<SectionLabel id={colorLabelId}>{copy.colorLabel}</SectionLabel>
							{originBadge("accentColor", copy.colorLabel)}
						</LabelRow>
						{/*
						 * The account sheet's swatch, unchanged and fully controlled. It never derives the
						 * foreground this accent will be read against: `onAccentColor` is computed
						 * server-side so every accent stays WCAG-safe, and a second opinion here would be
						 * the one that eventually disagrees.
						 */}
						<BrandColorPalette
							/*
							 * THIS TRANSFER'S OWN colour, and empty while it sets none, so the first swatch
							 * is checked exactly when the transfer sets no colour. The account's accent is
							 * deliberately NOT the value: it used to be, and then clearing changed nothing
							 * the row could show, the first swatch could never be checked, and the arrow keys
							 * got stuck on it (review of kitecorp/transferify-ui#2418). What an untouched
							 * transfer inherits is shown by the badge beside the label, the first swatch's
							 * name and the live preview.
							 */
							value={vm.draft.accentColor ?? ""}
							/*
							 * Five presets, not the full 36 (owner, 2026-09-03: "5 is fine"). This is a
							 * quick composer beside a live preview, not the account's brand settings —
							 * which is where a sender goes for any of the other 31.
							 */
							colors={PALETTE_PRESETS}
							disabled={inert || vm.uploading}
							labelledBy={colorLabelId}
							/*
							 * Clearing is a SWATCH in the row rather than a pill above it (owner
							 * 2026-09-12). It is always rendered, unlike the pill it replaces: a stop that
							 * appeared only once an accent was set could not be the chosen one, and
							 * "this transfer sets no colour of its own" is the state most senders are in.
							 */
							clear={{label: copy[accentClearLabelKey(vm.inherited.accentColor)], onClear: vm.clearAccentColor}}
							onPick={vm.setAccentColor}
						/>
					</Section>

					{/* THE FONT PICKER (owner addition 2026-09-01). Rendered from `vm.fonts`, which is the
					    api's own published list narrowed to the faces this build can paint — never a local
					    catalogue, for the same reason the backdrop arity is not one: a hard-coded copy
					    drifts, and the symptom is a picker offering a face the api answers 400 for.
					    Hidden entirely when the offer publishes none, rather than shown empty. */}
					{vm.fonts.length > 0 && (
						<Section>
							<LabelRow>
								<SectionLabel id={fontLabelId}>{copy.fontLabel}</SectionLabel>
							</LabelRow>
							<FontGrid role="radiogroup" aria-labelledby={fontLabelId}>
								{/* The standard face first, and free: absent on the wire, because "never
								    chose" and "chose the standard one" want the same page. */}
								<FontCard $selected={vm.draft.font === null}>
									<HiddenRadio
										type="radio"
										name="transfer-personalise-font"
										aria-label={copy.fontChoiceAria(copy.fontStandard)}
										value=""
										checked={vm.draft.font === null}
										disabled={inert}
										onChange={() => vm.setFont(null)}
									/>
									<FontSpecimen>{copy.fontSpecimen}</FontSpecimen>
									<FontDiacritics>{copy.fontDiacritics}</FontDiacritics>
									{/* The one tile that names itself the default, because it is the only one that
									    differs: it is the face a page has when the sender picks none. */}
									<FontName>{copy.fontStandard} · {copy.fontStandardHint}</FontName>
								</FontCard>
								{vm.fonts.map(font => {
									const family = personalizationFontFamily(font);
									return (
										<FontCard
											key={font}
											$selected={vm.draft.font === font}
											// The family is handed in as a custom property rather than named in
											// the stylesheet: the catalogue is the api's, and a stack written
											// into CSS would be a second copy of a list this repo deliberately
											// does not keep. It is a value, not a style definition, so the
											// externalized-styles rule is satisfied by the styles file that
											// consumes it.
											style={{"--tf-specimen-family": `'${family}'`} as React.CSSProperties}
										>
											<HiddenRadio
												type="radio"
												name="transfer-personalise-font"
												aria-label={copy.fontChoiceAria(family)}
												value={font}
												checked={vm.draft.font === font}
												disabled={inert}
												onChange={() => vm.setFont(font)}
											/>
											<FontSpecimen>{copy.fontSpecimen}</FontSpecimen>
											{/* The Romanian diacritics, in the offered face: the owner's hard
											    constraint is that every face carries ă â î ș ț, and this is
											    where a sender sees their own language before choosing. */}
											<FontDiacritics>{copy.fontDiacritics}</FontDiacritics>
											<FontName>{family}</FontName>
										</FontCard>
									);
								})}
							</FontGrid>
						</Section>
					)}

					<Section>
						<LabelRow>
							<SectionLabel>{copy.logoLabel}</SectionLabel>
							{originBadge("logo", copy.logoLabel)}
						</LabelRow>
						{/*
						 * THE TILE IS THE CONTROL (ui#1942, owner: "'alege logo' should be removed and the
						 * click moved onto the logo actual UI"). The visible "Choose a logo" button is gone;
						 * the picture opens the picker, and its accessible name is the wording that button
						 * used to carry — chosen by `logoPickLabelKey` rather than by a ternary here, because
						 * it is now the only thing a screen-reader user is told about this control.
						 *
						 * Empty, it is a dashed tile with a plus in it; filled, it boxes the mark so a wide or
						 * tall upload cannot push the sheet around, which is the job the old frame had.
						 */}
						<LogoTile>
							<LogoPickTile $disabled={inert || vm.uploading} $empty={vm.draft.logo === null}>
								{vm.draft.logo !== null
									? <img src={vm.draft.logo.url} alt={copy.logoAlt}/>
									: <LogoPickGlyph aria-hidden="true">+</LogoPickGlyph>}
								<HiddenFileInput
									type="file"
									accept={LOGO_ACCEPT_ATTRIBUTE}
									// A STABLE handle for the specs that assert this control's ENABLEMENT per
									// rung (ui#1942). Its accessible name now flips with the draft — that is
									// the point of the tile, and it is asserted as such in
									// `personalization-preview-first.spec.ts` — so a name-based locator would
									// silently stop resolving on whichever state it was not written for.
									data-testid={PERSONALISE_LOGO_PICK_TESTID}
									aria-label={copy[logoPickLabelKey(vm.draft.logo !== null)]}
									disabled={inert || vm.uploading}
									onChange={event => {
										const picked = event.target.files?.[0];
										event.target.value = "";
										if (picked) {
											void vm.uploadLogo(picked);
										}
									}}
								/>
							</LogoPickTile>
							{vm.draft.logo !== null && (
								/* On the tile's corner, the same place a picture tile's remove sits — and a
								   SIBLING of the label rather than a child, or every click on it would open the
								   picker as well. `vm.uploading` beside `inert` because a logo upload settling
								   AFTER a clear writes its asset back onto the cleared draft. */
								<LogoRemoveButton
									label={copy.removeLogo}
									disabled={inert || vm.uploading}
									onClick={vm.removeLogo}
								/>
							)}
						</LogoTile>
					</Section>

					<Section>
						<LabelRow>
							<SectionLabel>{copy.templateLabel}</SectionLabel>
							{originBadge("template", copy.templateLabel)}
						</LabelRow>
						{/* THE TILES (ui#1953). Which tiles, in which groups, is `vm.templateGroups`, the
						    `personalizationTemplateGroups` answer over the api's own offer and the campaign's
						    switch; the miniature above is the preview, so the tiles carry a glyph and a name
						    and nothing animates in here. Names and descriptions are the account picker's,
						    because these are the same treatments. */}
						<TemplateTilePicker
							groups={vm.templateGroups}
							value={vm.draft.template}
							onChange={vm.setTemplate}
							disabled={inert}
							name={TEMPLATE_RADIO_NAME}
							label={copy.templateLabel}
							copy={{
								categories: {
									BACKDROP: account.theme.categories.BACKDROP.name,
									WALLPAPER: account.theme.categories.WALLPAPER.name,
									SCENE: account.theme.categories.SCENE.name,
								},
								templates: account.theme.templates,
								none: {name: copy.templateNone, description: copy.templateNoneHint},
							}}
						/>
						{/* ONE line under the tiles, and which of three things it says is
						    `templateImagesLine`'s answer: the general invitation until a look is chosen,
						    the REQUIREMENT while its pictures are missing, and the plain arity once they
						    are there. The descriptions that used to sit under each card are the tiles'
						    tooltips now.

						    The requirement is an `alert` because it is the whole of what tells a sender
						    that pictures are mandatory: they choose the animation at this control and may
						    never scroll as far as the images section, which is exactly the dead end the
						    owner reported (ui#2416). */}
						{imagesLine.kind === "required"
							? <FieldHint id={imagesRequiredId} role="alert" $error>
								{copy.imagesRequired(imagesLine.minimum)}
							</FieldHint>
							: <FieldHint>
								{imagesLine.kind === "arity"
									? copy.imagesHint(imagesLine.minimum, imagesLine.maximum)
									: copy.templateHint}
							</FieldHint>}
					</Section>

					{/* THE WALLPAPER SETTINGS (kitecorp/transferify-api-service#1149).
					    The SAME panel the sponsor booking form mounts, over this builder's own state:
					    which controls appear comes from `TEMPLATE_OPTIONS`, the verdict from
					    `validateSponsorOptions`, and both are the modules the api's own
					    `BackdropOptionsValidator` is mirrored by. A second set of controls here would be a
					    setting an advertiser can buy that a sender cannot choose.

					    Mounted only when the api's own catalogue calls the chosen look a WALLPAPER, so a
					    build meeting an api that does not sell them yet offers nothing it would refuse.
					    The logo group is deliberately omitted: this sheet already has its own logo row,
					    which a text-only personalization uses too. */}
					{vm.takesOptions && vm.draft.template !== null && (
						<Section>
							<SponsorOptionsPanel
								template={vm.draft.template}
								options={vm.draft.options}
								setOption={vm.setOption}
								/* The SENDER's wording, shared with the account page-theme picker
								   (ui#1869): the same person is choosing the same settings for the same
								   kind of page, one surface over. */
								copy={account.theme.options}
								/* Its own prefix: three panels exist now, and radios sharing a name are
								   ONE exclusive group to the browser however they are nested. */
								namePrefix="transfer-personalise-option"
								/* No mark: this sheet already has its own logo row, above these controls
								   and shared with its free rung, so a second picker here would be two
								   controls writing one field. */
								logo={null}
								errorText={errorTextFor}
								/* The line first, under the lede (owner 2026-09-03): here the words are what
								   the sender came to write, and the rest are settings about them. A prop, so
								   the sponsor and account surfaces keep the order they were reviewed with. */
								sublineFirst
							/>
						</Section>
					)}

					{/* Held by ref so a Done that cannot commit can bring it into view (ui#2416): it sits
					    below the animation picker, and the sender who never scrolled this far is exactly
					    the one being sent here. */}
					<Section ref={imagesSectionRef}>
						<LabelRow>
							<SectionLabel>{copy.imagesLabel}</SectionLabel>
							{originBadge("images", copy.imagesLabel)}
							{/* The tally as the label's chip (ui#1953), where the prototype puts it; the
							    chosen look's floor is the hint under the tiles. */}
							{vm.arity !== null && (
								<Counter>{copy.counter(vm.draft.images.length, vm.arity.maximumImages)}</Counter>
							)}
						</LabelRow>
						<ImageRow>
							{vm.draft.images.map((image, index) => {
								const position = index + 1;
								return (
									<ImageTile key={image.assetId}>
										{/* Decorative: the sender is looking at their own upload, and every
										    control beside it is labelled by position. */}
										<TileImage src={image.url} alt="" $inTheme/>
										{/* The account editor's control, shared rather than re-implemented
										    (ui#760). It drops the picture from THIS DRAFT only. */}
										<TileRemoveButton
											label={copy.removeImageAria(position)}
											disabled={inert || vm.uploading}
											onClick={() => vm.removeImage(image.assetId)}
										/>
									</ImageTile>
								);
							})}
							<AddImageLabel $disabled={inert || vm.uploading || vm.draft.template === null}>
								{vm.uploading ? copy.uploading : copy.addImage}
								<HiddenFileInput
									ref={addImageRef}
									type="file"
									multiple
									accept={THEME_IMAGE_ACCEPT_ATTRIBUTE}
									aria-label={copy.addImageAria}
									// No backdrop means no arity to admit against and nothing for the pictures
									// to render into — the api refuses that pair outright (api#1099), so the
									// chooser is closed until an animation is chosen.
									disabled={inert || vm.uploading || vm.draft.template === null}
									onChange={event => {
										const picked = Array.from(event.target.files ?? []);
										// Cleared BEFORE dispatching so re-picking the same files after a
										// rejection still fires a change event.
										event.target.value = "";
										if (picked.length > 0) {
											void vm.uploadImages(picked);
										}
									}}
								/>
							</AddImageLabel>
						</ImageRow>
						{vm.overflow > 0 && vm.arity !== null && (
							<FieldHint role="status">{copy.overflow(vm.overflow, vm.arity.maximumImages)}</FieldHint>
						)}
					</Section>
				</div>
			)}
			</BuilderControls>
			</BuilderLayout>}
		</CardSheet>
	);
}
